import React from "react";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, Heart, MessageCircle, Compass, CalendarDays, ArrowRight } from "lucide-react";
import { ATTUNEMENTS, SITE } from "@/lib/site";

const waUrl = (message) =>
  `https://wa.me/${SITE.phoneClean}?text=${encodeURIComponent(message)}`;

const Attunements = () => {
  if (!ATTUNEMENTS?.length) return null;

  return (
    <section id="attunements" data-testid="attunements-section" className="py-24 lg:py-32 bg-[#F4F1EB] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 h-[26rem] w-[26rem] rounded-full bg-[#C5A880]/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[22rem] w-[22rem] rounded-full bg-[#8A9A86]/15 blur-3xl" />

      <div className="mx-auto max-w-7xl px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#B35B47]">
              <Sparkles className="h-3.5 w-3.5 text-[#C5A880]" />
              — Reiki Attunements
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#2C302E] leading-[1.05]">
              Sacred initiations<br />into your own light.
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#5C605A] leading-relaxed">
            Attunements are deeply personal, ceremonial energy transmissions
            that gently open you to a specific stream of healing and
            manifestation. Each one is held one-on-one with devotion and care.
          </p>
        </div>

        <div className="space-y-10 lg:space-y-14">
          {ATTUNEMENTS.map((a, i) => {
            const reverse = i % 2 === 1;
            return (
            <motion.article
              key={a.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={`attunement-${a.id}`}
              className={`grid lg:grid-cols-12 gap-8 lg:gap-14 bg-[#FAF9F6] rounded-[2rem] overflow-hidden border border-[#EAE5D9]`}
            >
              <div className={`lg:col-span-5 relative ${reverse ? "lg:order-2" : ""}`}>
                <div className="h-full min-h-[22rem] lg:min-h-[32rem] relative overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              </div>

              <div className={`lg:col-span-7 p-7 lg:p-12 ${reverse ? "lg:pl-14" : "lg:pr-14"}`}>
                <div className="text-xs uppercase tracking-[0.28em] text-[#B35B47]">
                  Attunement
                </div>
                <h3 className="font-serif text-3xl lg:text-5xl mt-3 text-[#2C302E] leading-[1.05]">
                  {a.name}
                </h3>
                <div className="mt-3 font-serif italic text-lg text-[#5C605A]">
                  {a.subtitle}
                </div>
                {a.mantra && (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 text-[#B35B47] text-xs uppercase tracking-[0.22em] px-4 py-2">
                    <Sparkles className="h-3 w-3 text-[#C5A880]" />
                    {a.mantra}
                  </div>
                )}

                {Array.isArray(a.description) ? (
                  <div className="mt-6 space-y-4 text-[#5C605A] leading-relaxed text-lg">
                    {a.description.map((p, idx) => (
                      <p key={idx}>{p}</p>
                    ))}
                  </div>
                ) : (
                  <p className="mt-6 text-[#5C605A] leading-relaxed text-lg">
                    {a.description}
                  </p>
                )}

                <div className="mt-8">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#8A8D86] mb-4">
                    {a.exploreTitle || "What you may explore"}
                  </div>
                  <ul className="space-y-3">
                    {a.explore.map((e, idx) => (
                      <li key={idx} className="flex gap-3 text-[#2C302E]">
                        <CheckCircle2 className="h-5 w-5 text-[#C5A880] shrink-0 mt-0.5" strokeWidth={1.6} />
                        <span className="leading-relaxed">{e}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {a.process && (
                  <div className="mt-8 rounded-2xl bg-[#F4F1EB] border border-[#EAE5D9] p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B35B47]">
                      <Compass className="h-3.5 w-3.5 text-[#C5A880]" strokeWidth={1.6} />
                      {a.process.heading || "How does the process work"}
                    </div>
                    <p className="mt-4 text-[#2C302E] leading-relaxed">{a.process.body}</p>
                  </div>
                )}

                {a.sessionDetails && (
                  <div className="mt-8 rounded-2xl bg-[#2C302E] text-[#FAF9F6] p-7 lg:p-8">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#C5A880]">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {a.sessionDetails.heading}
                    </div>
                    {a.sessionDetails.intro && (
                      <p className="mt-4 text-[#FAF9F6]/80 leading-relaxed text-sm">
                        {a.sessionDetails.intro}
                      </p>
                    )}
                    <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-5">
                      {a.sessionDetails.fields.map((f, idx) => (
                        <div key={idx}>
                          <dt className="text-[11px] uppercase tracking-[0.22em] text-[#C5A880]/80">{f.label}</dt>
                          <dd className="mt-1 text-[#FAF9F6]">{f.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {a.registration && (
                  <div className="mt-8">
                    <div className="text-xs uppercase tracking-[0.22em] text-[#B35B47]">
                      {a.registration.heading || "How to register"}
                    </div>
                    {a.registration.intro && (
                      <p className="mt-3 font-serif italic text-xl text-[#2C302E] leading-snug">
                        {a.registration.intro}
                      </p>
                    )}
                    {a.registration.body && (
                      <p className="mt-4 text-[#5C605A] leading-relaxed">
                        {a.registration.body}
                      </p>
                    )}
                    {a.registration.outro && (
                      <p className="mt-4 font-serif italic text-[#8A8D86] leading-relaxed">
                        {a.registration.outro}
                      </p>
                    )}
                  </div>
                )}

                {a.forWhom && (
                  <div className="mt-8 rounded-2xl bg-[#F4F1EB] border border-[#EAE5D9] p-5">
                    <div className="flex items-start gap-3">
                      <Heart className="h-5 w-5 text-[#B35B47] shrink-0 mt-0.5" strokeWidth={1.6} />
                      <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-[#8A8D86] mb-1">
                          Who is it for
                        </div>
                        <p className="text-[#2C302E] leading-relaxed">{a.forWhom}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex flex-wrap items-center gap-5 justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[#8A8D86]">{a.exchangeLabel || "Energy Exchange"}</div>
                    <div className="font-serif text-3xl text-[#B35B47] mt-1">{a.price}</div>
                  </div>
                  <a
                    href={waUrl(a.bookingMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`attunement-book-${a.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#2C302E] text-[#FAF9F6] px-7 py-3.5 text-sm tracking-wide hover:bg-[#B35B47] transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {a.bookingLabel}
                  </a>
                </div>
              </div>
            </motion.article>
          );})}
        </div>
      </div>
    </section>
  );
};

export default Attunements;
